import { AdminField, CompanyField } from 'components/shared/dropdowns'
import { Box, FormModal, SpinnerButton, TextField, useRouter, useSnackbar } from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { GetAllOfferingsDocument, useCreateOfferingMutation } from 'graphql/bindings/AdminBindings'
import { MutationResult } from 'react-apollo'
import { Routes } from 'util/Routes'
import React from 'react'

interface FormShape {
  id?: string
  owner?: { label: string; value: number }
  issuer?: { label: string; value: number }
  campaign_type?: string
  campaign_name?: string
  structure?: string
  mutationResult: MutationResult
}

const CampaignForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, form, values, valid }) => {
  const mutateInProgress = values.mutationResult.called
  const allRequiredFieldsFilled = [values?.campaign_type, values?.issuer, values?.owner].every(field => field)
  return (
    <form onSubmit={handleSubmit}>
      <Field name="campaign_type" component={TextField} label="Campaign Type" fullWidth disabled />
      <Field name="campaign_name" component={TextField} label="Campaign Name" fullWidth />
      <CompanyField name="issuer" label="Issuer (required)" />
      <AdminField name="owner" label="Owner" />
      <Field name="structure" component={TextField} label="Structure" disabled fullWidth />
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          disabled={!allRequiredFieldsFilled}
          loading={mutateInProgress}
          caption="Create"
        />
      </Box>
    </form>
  )
}

const CreateCampaignModal: React.FC<{ hideModal: () => void }> = ({ hideModal }) => {
  const { history } = useRouter()
  const { enqueueSnackbar } = useSnackbar()
  const [mutate, mutationResult] = useCreateOfferingMutation({
    refetchQueries: [{ query: GetAllOfferingsDocument }],
    onCompleted: data => {
      hideModal()
      history.push(Routes.campaignForm(data.createOffering.id))
    },
    onError: error => {
      const errorMessage = error.graphQLErrors.reduce((message, error) => {
        return `${message}${error.message} \n`
      }, '')
      enqueueSnackbar(errorMessage, { variant: 'error' })
    },
  })

  const onSubmit = (values: FormShape) => {
    mutate({
      variables: {
        form: {
          company_id: values?.issuer?.value ?? 0,
          name: values?.campaign_name ?? '',
          owner_ids: [values?.owner?.value ?? 0],
        },
      },
    })
  }

  const initialValues: FormShape = {
    campaign_type: 'Offering',
    structure: 'Fixed Price, Supply Side Offering (FCFF)',
    mutationResult,
  }

  return (
    <FormModal open hideModal={hideModal} title="Create Campaign">
      <Form<FormShape>
        component={CampaignForm}
        onSubmit={onSubmit}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}

export { CreateCampaignModal }
